import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeat,
  CharacterFeature,
  CharacterASIBaseFeature,
  ASIBaseFeature,
  CharacterASIFeature,
} from "@dndModels";
import { DisablableOption } from "@models";

// import DataUtilities from '../../../utilities/common_utilities';

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CharacterFeatBoxOld from "@dndComponents/character/feats/CharacterFeatBoxOld";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import LoadingBox from "@components/input/LoadingBox";
import CheckBox from "@components/input/CheckBox";
import CharacterASIFeatureInput from "@dndComponents/character/features/CharacterASIFeatureInput";

interface Props {
  character: Character;
  obj: CharacterFeature;
}

// const common_util = DataUtilities.getInstance();

export default function CharacterASIBaseFeatureInput({ 
  obj,
  character,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [ping, setPing] = useState(false);
  const [options, setOptions] = useState<DisablableOption[]>([]);
  const [show_flex, setShowFlex] = useState(false);
  const [flex_mode, setFlexMode] = useState(false);
  const [reloading, setReloading] = useState(false);

  const load = useCallback(() => {
    setFetched(true);
    if (obj.feature && obj.feature.floating_feature) {
      const character_asi_base_feature = obj.feature_options[0] as CharacterASIBaseFeature;
      const asi_base_feature = obj.feature.floating_feature as ASIBaseFeature;
      if (character.c_options.asi_flexibility) {
        const choice = asi_base_feature.choices.length === 1 ? asi_base_feature.choices[0] : asi_base_feature.choices.find(o => o.true_id === character_asi_base_feature.selected_choice_id);
        if (choice && choice.asi_features.filter(o => o.options.length < 6).length > 0) {
          setShowFlex(true);
          // Check if they're already using it
          if (!asi_base_feature.feat_option || (character_asi_base_feature.use_feat !== null && !character_asi_base_feature.use_feat)) {
            for (const asi_feature of choice.asi_features) {
              const character_asi_feature = character_asi_base_feature.asi_features.find(o => o.true_id === asi_feature.true_id);
              if (character_asi_feature && character_asi_feature.selected_option !== "None" && !asi_feature.options.includes(character_asi_feature.selected_option)) {
                setFlexMode(true);
                break;
              }
            }
          }
        }
      }
      setOptions(asi_base_feature.choices.map(o => {
        const opt: DisablableOption = {
          id: o.true_id, text: o.label
        };
        return opt;
      }));
    }
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderFeatOption = (asi_base_feature: ASIBaseFeature, character_asi_base_feature: CharacterASIBaseFeature) => {
    if (asi_base_feature.feat_option) {
      const feat_value = character_asi_base_feature.use_feat === null ? "Choose an Option" : character_asi_base_feature.use_feat ? "Feat" : "Ability Score Improvement";
      return (
        <Grid size={12}>
          <SelectStringBox
            name="Use Feat?"
            options={["Choose an Option","Ability Score Improvement","Feat"]}
            value={feat_value}
            color={ feat_value === "Choose an Option" ? "blue" : "" }
            onChange={(value: string) => {
              if (value === "Choose an Option") {
                character_asi_base_feature.use_feat = null;
              } else if (value === "Feat") {
                character_asi_base_feature.use_feat = true;
                character_asi_base_feature.feat = new CharacterFeat();
                for (let i = 0; i < asi_base_feature.asi_features.length; i++) {
                  const character_asi_feature = character_asi_base_feature.asi_features[i];
                  character_asi_feature.selected_option = "None";
                }
              } else {
                character_asi_base_feature.use_feat = false;
              }
              character.changed();
              character.subscription_holder.recalculate();
              setPing(!ping);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderSelection = (asi_base_feature: ASIBaseFeature, character_asi_base_feature: CharacterASIBaseFeature) => {
    // debugger;
    if (!asi_base_feature.feat_option || (character_asi_base_feature.use_feat !== null && !character_asi_base_feature.use_feat)) {
      const asi_choice = asi_base_feature.choices.length === 1 ? asi_base_feature.choices[0] : asi_base_feature.choices.find(o => o.true_id === character_asi_base_feature.selected_choice_id);
      // console.log(asi_base_feature.choices);
      // console.log(character_asi_base_feature.selected_choice_id);
      // console.log(asi_base_feature.choices.find(o => o.true_id === character_asi_base_feature.selected_choice_id));
      if (asi_choice) {
        return asi_choice.asi_features.map(asi_feature => {
          const character_asi_feature = character_asi_base_feature.asi_features.find(o => 
            o.true_id === asi_feature.true_id);
          if (character_asi_feature) {
            return (
              <Grid key={asi_feature.true_id}
                size={12}>
                <CharacterASIFeatureInput
                  obj={obj}
                  character={character}
                  character_asi_base_feature={character_asi_base_feature}
                  character_asi_feature={character_asi_feature}
                  asi_feature={asi_feature}
                  asi_base_feature={asi_base_feature}
                  flex_mode={flex_mode}
                />
              </Grid>
            );
          }
          return null;
        });
      }
    } else if (asi_base_feature.feat_option && character_asi_base_feature.use_feat) {
      return (
        <Grid size={12}>
          <CharacterFeatBoxOld
            obj={character_asi_base_feature.feat}
            character={character}
            onChange={(changed: CharacterFeat) => {
              character_asi_base_feature.feat = changed;
              character.changed();
              character.subscription_holder.recalculate();
              setPing(!ping);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderFlex = () => {
    const asi_base_feature = obj.floating_feature;
    if (show_flex && !(asi_base_feature instanceof ASIBaseFeature && asi_base_feature.asi_features.length === 1 && asi_base_feature.asi_features[0].amount === 0)) {
      return (
        <Grid size={12}>
          <CheckBox
            name="Flexible"
            value={flex_mode}
            onChange={(changed: boolean) => {
              setFlexMode(changed);
              setReloading(true);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  if (!fetched) {
    return (<LoadingBox />);
  } else if (obj.feature && obj.floating_feature instanceof ASIBaseFeature) {
    // debugger;
    const asi_base_feature = obj.floating_feature;
    const character_asi_base_feature = obj.feature_options[0] as CharacterASIBaseFeature;
    if (asi_base_feature.choices.length === 1) {
      return (
        <Grid container spacing={0} direction="row" className="padded">
          <Grid className="header centered" size={12}>{ obj.name }</Grid>
          <Grid size={12}><DisplayStringWithReplacements display_string={ obj.description } cfs={character} /></Grid>
          { renderFlex() }
          { renderFeatOption(asi_base_feature, character_asi_base_feature) }
          { renderSelection(asi_base_feature, character_asi_base_feature) }
        </Grid>
      );
    } else {
      // const selected_choice = asi_base_feature.choices.find(o => o.true_id === character_asi_base_feature.selected_choice_id);
      // console.log(options);
      return (
        <Grid container spacing={0} direction="row" className="padded">
          <Grid className="header centered" size={12}>{ obj.name }</Grid>
          <Grid size={12}><DisplayStringWithReplacements display_string={ obj.description } cfs={character} /></Grid>
          <Grid size={12}>
            <SelectStringBox
              name="Select an ASI Option"
              options={options}
              value={character_asi_base_feature.selected_choice_id}
              // log_it
              onChange={(changed: string) => {
                const cabf = obj.feature_options[0] as CharacterASIBaseFeature;
                cabf.selected_choice_id = changed;
                const choice = asi_base_feature.choices.find(o => o.true_id === changed);
                let new_show_flex = false;
                if (choice) {
                  cabf.asi_features = [];
                  for (const asif of choice.asi_features) {
                    const feature = new CharacterASIFeature();
                    feature.copyASIFeature(asif);
                    // feature.id = character_asi_base_feature.asi_features.length;
                    cabf.asi_features.push(feature);
                  }
                  if (choice.asi_features.filter(o => o.options.length < 6).length > 0) {
                    new_show_flex = true;
                  }
                } else {
                  cabf.asi_features = [];
                }
                // obj.feature_options[0] = changed;
                character.changed();
                character.subscription_holder.recalculate();
                setShowFlex(new_show_flex);
              }}
            />
          </Grid>
          { renderFlex() }
          { renderSelection(asi_base_feature, character_asi_base_feature) }
        </Grid>
      );
    }
  }
  return <></>;
}
